import { useState, useImperativeHandle, forwardRef, useEffect, useCallback, useRef } from "react";
// @ts-ignore
import { ContentState, EditorState, convertToRaw } from "draft-js";
// @ts-ignore
import draftToHtml from "draftjs-to-html";
// @ts-ignore
import { Editor as DraftEditor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
// @ts-ignore
import htmlToDraft from "html-to-draftjs";

interface WysiwygProps {
  display: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  rows?: number;
  readOnly?: boolean;
  placeholder?: string;
  editorStyle?: any;
  editorClassName?: string;
  imageFolderLocation?: string;
  error?: string;
  touched?: boolean;
  required?: boolean;
}

const Wysiwyg = forwardRef(
  (
    {
      display,
      value: initialValue = "", // alias 'value' to 'initialValue'
      onChange,
      onBlur,
      rows = 5,
      readOnly = false,
      placeholder = "Start typing...",
      editorStyle = {},
      editorClassName,
      error,
      touched,
      required,
      imageFolderLocation = "images/wysiwyg/defaultFolder",
    }: WysiwygProps,
    ref: any,
  ) => {
    
    // Helper: Convert HTML string to EditorState
    const prepareDraft = (value: string) => {
      const draft = htmlToDraft(value);
      if (draft) {
        const contentState = ContentState.createFromBlockArray(
          draft.contentBlocks,
          draft.entityMap
        );
        return EditorState.createWithContent(contentState);
      }
      return EditorState.createEmpty();
    };

    // 1. Initialize State ONLY once on mount
    const [editorState, setEditorState] = useState(() =>
      initialValue ? prepareDraft(initialValue) : EditorState.createEmpty()
    );

    // 2. Use a Ref to track the last value sent to the parent
    // This prevents the "loop" that causes the cursor to jump back
    const lastHtmlRef = useRef(initialValue);

    // 3. Handle External Updates (e.g., loading data from API or form reset)
    useEffect(() => {
      // Only update local state if the incoming value is different from 
      // the last value we deliberately sent out.
      if (initialValue !== lastHtmlRef.current) {
        const newContent = initialValue ? prepareDraft(initialValue) : EditorState.createEmpty();
        setEditorState(newContent);
        // Sync the ref so we don't re-trigger immediately
        lastHtmlRef.current = initialValue; 
      }
    }, [initialValue]);

    // 4. Handle User Typing
    const onEditorStateChange = useCallback(
      (newEditorState: EditorState) => {
        setEditorState(newEditorState);

        const content = draftToHtml(
          convertToRaw(newEditorState.getCurrentContent())
        );

        // Clean up empty paragraphs if necessary
        const filteredContent = content === "<p></p>\n" ? "" : content;

        // Only fire onChange if content actually changed to avoid unnecessary renders
        if (onChange && filteredContent !== lastHtmlRef.current) {
          lastHtmlRef.current = filteredContent; // Update our tracker
          onChange(filteredContent);
        }
      },
      [onChange]
    );

    // Expose method via ref
    useImperativeHandle(ref, () => ({
      getHtmlContent: () => {
        return draftToHtml(convertToRaw(editorState.getCurrentContent()));
      },
    }));

    const uploadCallback = (file: File) => {
      const fileName = file.name;
      const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
      const newFileName = `${timestamp}_${fileName}`;
      return new Promise<{ data: { link: string } }>((resolve) => {
        setTimeout(
          () => resolve({ data: { link: `/path/to/${newFileName}` } }),
          1000
        );
      });
    };

    return (
      <div className="relative mb-4">
        {display && (
          <div className="text-instant-teams-teal-Main font-bold pl-1 pb-3">
            {display}
            {required && " *"}
            {readOnly ? " - LOCKED" : null}
          </div>
        )}
        
        {/* @ts-ignore */}
        <DraftEditor
          readOnly={readOnly}
          spellCheck={true}
          editorStyle={editorStyle}
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          wrapperClassName="shadow-md rounded-md bg-white"
          editorClassName={
            editorClassName || `pl-4 pr-4 h-${rows * 12}`
          }
          toolbarClassName="border-solid border-b-2 border-sky-500 p-2 text-xs"
          placeholder={placeholder}
          toolbar={{
            options: [
              "inline",
              "list",
              "textAlign",
            ],
            // ADDED: Configuration for the blockType dropdown
            blockType: {
              inDropdown: true,
              options: [
                "Normal",
                "H1",
                "H2",
                "H3",
                "H4",
                "H5",
                "H6",
                "Blockquote",
                "Code",
              ],
            },
            inline: {
              options: ["bold", "italic", "underline"],
            },
            list: {
              options: ["unordered", "ordered"],
            },
            textAlign: {
              options: ["left", "center", "right", "justify"],
            },
            image: {
              uploadCallback: uploadCallback,
            },
          }}
          onBlur={onBlur}
        />

        {error && (
          <p className={"text-[0.8rem] font-medium text-[red] mt-1"}>{error}</p>
        )}
      </div>
    );
  }
);

export default Wysiwyg;